// 前端版本自動更新：開著的分頁定期讀取 version.json，發現正式機版本不同時，
// 1. 換頁時趁機整頁載入目標頁；2. 閒置很久（不論頁面、是否在編輯）就直接重新整理。
// 目的是避免分頁一直停在舊版，不追求部署後立刻換新。
// 版本號由 CI 寫入 VUE_APP_BUILD_ID；本機開發沒有版本號時整個機制不啟動。

export const BUILD_ID = process.env.VUE_APP_BUILD_ID || ''

const MINUTE = 60 * 1000
export const CHECK_INTERVAL = 10 * MINUTE // 例行檢查間隔
export const ROUTE_CHECK_GAP = 5 * MINUTE // 換頁時距離上次檢查超過此時間才再查
export const VISIBLE_CHECK_GAP = MINUTE // 切回分頁時的最短檢查間隔，避免來回切換一直查
export const IDLE_RELOAD_AFTER = 4 * 60 * MINUTE // 閒置多久直接刷新；比診所午休長，隔夜一定會觸發
export const RETRY_GUARD = 10 * MINUTE // 同一個目標版本在這段時間內只刷新一次，避免快取異常時無限刷新
export const FETCH_TIMEOUT = 10 * 1000 // version.json 請求逾時，避免卡住後不再檢查
export const RELOAD_LOCK_RESET = 30 * 1000 // 整頁載入被取消（例如離開提醒按取消）後，頁面仍在就解除鎖定
const TICK = 30 * 1000
const ATTEMPT_KEY = 'crm-app-update-attempt'
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll']

export function createAppUpdater({
  currentBuild = BUILD_ID,
  versionUrl = `${process.env.BASE_URL || '/'}version.json`,
  win = typeof window === 'undefined' ? undefined : window,
  fetchImpl = typeof fetch === 'undefined' ? undefined : (...args) => fetch(...args),
  storage = safeSessionStorage(win),
  now = () => Date.now(),
  isInLiff = () => false,
  fetchTimeout = FETCH_TIMEOUT,
} = {}) {
  const state = { started: false, pending: false, latestBuild: '', lastCheckAt: 0, lastActivityAt: 0 }
  let checking = null
  let reloading = false
  let timer = null

  const isHidden = () => !!(win && win.document && win.document.visibilityState === 'hidden')
  const isIdle = () => now() - state.lastActivityAt >= IDLE_RELOAD_AFTER

  function readAttempt() {
    try { return JSON.parse(storage.getItem(ATTEMPT_KEY)) } catch (e) { return null }
  }

  // 每次整頁載入前先記錄；同一目標版本短時間內不重試。sessionStorage 無法使用時不刷新，避免失去防護。
  function reloadWith(perform) {
    if (!state.pending || reloading || !storage) return false
    const attempt = readAttempt()
    if (attempt && attempt.build === state.latestBuild && now() - attempt.at < RETRY_GUARD) return false
    try { storage.setItem(ATTEMPT_KEY, JSON.stringify({ build: state.latestBuild, at: now() })) } catch (e) { return false }
    reloading = true
    win.setTimeout(() => { reloading = false }, RELOAD_LOCK_RESET)
    perform()
    return true
  }

  function reloadIfIdle() {
    if (state.pending && isIdle()) reloadWith(() => win.location.reload())
  }

  function check() {
    if (!state.started) return Promise.resolve()
    if (checking) return checking
    state.lastCheckAt = now()
    checking = (async () => {
      const controller = typeof AbortController === 'undefined' ? null : new AbortController()
      let timeoutId
      const timeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => {
          if (controller) controller.abort()
          reject(new Error('version check timeout'))
        }, fetchTimeout)
      })
      try {
        const request = (async () => {
          const response = await fetchImpl(versionUrl, { cache: 'no-store', credentials: 'omit', signal: controller ? controller.signal : undefined })
          // 正式機不存在的路徑會回 404 加上 index.html，不是 JSON，一律當作讀取失敗。
          if (!response || !response.ok) return null
          return response.json()
        })()
        request.catch(() => {}) // 逾時後被中止的請求不留下未處理的錯誤
        const data = await Promise.race([timeout, request])
        if (!data || typeof data.build !== 'string' || !data.build) return
        state.latestBuild = data.build
        // 只看是否不同、不比大小，退版時開著的分頁也會換回。
        state.pending = data.build !== currentBuild
        reloadIfIdle()
      } catch (e) {
        // 讀取失敗或逾時一律忽略，照常使用，下次再檢查。
      } finally {
        clearTimeout(timeoutId)
        checking = null
      }
    })()
    return checking
  }

  function checkIfStale(gap) {
    if (now() - state.lastCheckAt >= gap) check()
  }

  const markActivity = () => { state.lastActivityAt = now() }

  function onVisibilityChange() {
    if (!isHidden()) checkIfStale(VISIBLE_CHECK_GAP)
  }

  function onTick() {
    checkIfStale(CHECK_INTERVAL)
    reloadIfIdle()
  }

  // 換頁時改用整頁載入目標頁；放在 beforeResolve，各頁的離開提醒與登入導向都已處理完。
  function installRouterGuards(router) {
    router.beforeResolve((to, from, next) => {
      const isFirstLoad = !from.matched.length
      if (!isFirstLoad && to.path !== from.path &&
          reloadWith(() => win.location.assign(router.resolve(to.fullPath).href))) {
        return next(false)
      }
      next()
    })
    router.afterEach(() => checkIfStale(ROUTE_CHECK_GAP))
  }

  function start(router) {
    if (state.started || !currentBuild || !win || !fetchImpl || isInLiff()) return false
    state.started = true
    state.lastCheckAt = now() // 剛載入就是當下最新版，不需要立刻檢查
    state.lastActivityAt = now()
    for (const type of ACTIVITY_EVENTS) win.addEventListener(type, markActivity, { capture: true, passive: true })
    win.document.addEventListener('visibilitychange', onVisibilityChange)
    timer = win.setInterval(onTick, TICK)
    if (router) installRouterGuards(router)
    return true
  }

  function stop() {
    if (!state.started) return
    state.started = false
    for (const type of ACTIVITY_EVENTS) win.removeEventListener(type, markActivity, { capture: true })
    win.document.removeEventListener('visibilitychange', onVisibilityChange)
    win.clearInterval(timer)
  }

  return { state, start, stop, check, reloadIfIdle, reloadWith }
}

// 只對 CRM 後端帶版本號：其他來源（廣告、api.dentco.tw 等）的 CORS 不一定允許自訂 header。
export function installBuildHeader(http, { build = BUILD_ID, apiUrl = process.env.VUE_APP_API_URL } = {}) {
  if (!build || !apiUrl) return false
  let apiOrigin
  try { apiOrigin = new URL(apiUrl).origin } catch (e) { return false }
  http.interceptors.request.use(config => {
    try {
      const target = new URL(config.url || '', config.baseURL || apiUrl)
      if (target.origin === apiOrigin) {
        if (config.headers && typeof config.headers.set === 'function') config.headers.set('X-CRM-Build', build)
        else config.headers = { ...config.headers, 'X-CRM-Build': build }
      }
    } catch (e) {
      // 網址無法解析時不帶版本號，照常送出。
    }
    return config
  })
  return true
}

function safeSessionStorage(win) {
  try { return win && win.sessionStorage } catch (e) { return undefined }
}
